import { observer } from "mobx-react-lite";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import React, { useState, useEffect } from "react";

/**
 * Unified group sidebar component.
 *
 * Props:
 *   title     — header text (e.g. "Compounds", "Patent Compounds")
 *   icon      — icon class (e.g. "icon-math", "icon-copy")
 *   items     — Array<{ id: string, label: string, deletable?: boolean, children?: { id: string, label: string }[] }>
 *   selectedId — currently selected item id (or null)
 *   onSelect  — (item) => void
 *   onCreate? — async (name) => void       — shows "+ <createLabel>" button
 *   onOpenCreate? — () => void            — when set, clicking "+ ..." calls this instead of built-in dialog
 *   onDelete? — async (item) => void      — shows trash icon on deletable items
 *   itemCount? — number                   — optional count badge in header
 *   createIndent? — boolean               — when true, render create button inside children area (indented)
 *   createLabel? — string                 — label for create button (default "New Dataset")
 *   createDialogTitle? — string           — override dialog title (default: "New {title}")
 *   mergeHeader? — boolean                — when true, merge first item's label + icon into header (bolded), skip parent row
 */
const GroupSidebar = observer(({
  title,
  icon,
  items = [],
  selectedId,
  onSelect,
  onCreate,
  onOpenCreate,
  onDelete,
  itemCount,
  createIndent,
  createLabel = "New Dataset",
  createDialogTitle,
  mergeHeader,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [expandedParents, setExpandedParents] = useState({});
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newName, setNewName] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // By default, expand all parents that have children
  useEffect(() => {
    const init = {};
    items.forEach((item) => {
      if (item.children && item.children.length > 0) {
        init[item.id] = true;
      }
    });
    setExpandedParents((prev) => {
      if (Object.keys(prev).length === 0) return init;
      return prev;
    });
  }, [items]);

  const toggleParent = (id) => {
    setExpandedParents((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCreate = async () => {
    if (!newName.trim() || !onCreate) return;
    await onCreate(newName.trim());
    setNewName("");
    setShowNewDialog(false);
  };

  const handleDelete = async (item) => {
    if (!onDelete) return;
    await onDelete(item);
    setDeleteConfirm(null);
  };

  const hasCreate = !!onCreate;
  const hasDelete = !!onDelete;
  const showUngrouped = items.length > 0 && items[items.length - 1]?.id === "__ungrouped__";

  /** Render a single item row (used for both parent and child items) */
  const renderItemRow = (item, { isChild = false, isLastChild = false } = {}) => {
    const isSelected = selectedId === item.id;
    return (
      <div
        key={item.id}
        className={`flex align-items-center gap-1 px-2 py-1 border-round cursor-pointer transition-colors ${
          isSelected ? "bg-primary bg-primary-reverse font-bold" : "hover:bg-50"
        }`}
        style={{
          fontSize: "0.8rem",
          borderRadius: 6,
          position: "relative",
          background: isSelected ? "#eef2ff" : "transparent",
          color: isSelected ? "#4f46e5" : "#374151",
          fontWeight: isSelected ? 600 : 400,
          transition: "all 0.12s ease",
          marginLeft: isChild ? 0 : 0,
        }}
        onClick={() => onSelect && onSelect(item)}
        onMouseEnter={(e) => {
          if (!isSelected) e.currentTarget.style.background = "#f1f5f9";
        }}
        onMouseLeave={(e) => {
          if (!isSelected) e.currentTarget.style.background = "transparent";
        }}
      >
        {/* Connector line for child items */}
        {isChild && (
          <span className="flex align-items-center" style={{ width: 10 }}>
            <span style={{ width: 8, height: 1, background: "#cbd5e1", display: "inline-block" }} />
          </span>
        )}

        {/* Selection indicator */}
        {isSelected ? (
          <i className="pi pi-circle-fill" style={{ fontSize: "0.45rem", color: "#4f46e5" }} />
        ) : (
          <i className="pi pi-circle" style={{ fontSize: "0.45rem", color: "#cbd5e1" }} />
        )}

        <span
          className="flex-1"
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.label}
        </span>

        {/* Delete button */}
        {hasDelete && item.deletable && (
          <i
            className="pi pi-trash cursor-pointer"
            style={{
              fontSize: "0.65rem",
              color: isSelected ? "#a5b4fc" : "#94a3b8",
              opacity: 0,
              transition: "opacity 0.12s ease",
            }}
            onMouseEnter={(e) => { e.stopPropagation(); e.currentTarget.style.color = "#ef4444"; }}
            onMouseLeave={(e) => { e.stopPropagation(); e.currentTarget.style.color = isSelected ? "#a5b4fc" : "#94a3b8"; }}
            onClick={(e) => { e.stopPropagation(); setDeleteConfirm(item); }}
            title="Delete"
          />
        )}

        {/* Active indicator bar */}
        {isSelected && (
          <span
            style={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              width: 3,
              height: 16,
              background: "#4f46e5",
              borderRadius: "0 3px 3px 0",
            }}
          />
        )}
      </div>
    );
  };

  /** Render children list with connector line */
  const renderChildren = (children, parentId) => (
    <div
      key={`children-${parentId}`}
      className="flex flex-column"
      style={{
        marginLeft: 18,
        borderLeft: "1px solid #e2e8f0",
        paddingLeft: 6,
        marginTop: 2,
        marginBottom: 2,
      }}
    >
      {children.map((child, idx) => (
        <div
          key={child.id}
          style={{ position: "relative" }}
          onMouseEnter={(e) => {
            const deleteBtn = e.currentTarget.querySelector(".pi-trash");
            if (deleteBtn) deleteBtn.style.opacity = "1";
          }}
          onMouseLeave={(e) => {
            const deleteBtn = e.currentTarget.querySelector(".pi-trash");
            if (deleteBtn) deleteBtn.style.opacity = "0";
          }}
        >
          {renderItemRow(child, { isChild: true, isLastChild: idx === children.length - 1 })}
        </div>
      ))}

      {/* Indented create button inside children area */}
      {hasCreate && createIndent && (
        <div
          className="flex align-items-center gap-2 px-2 py-1 cursor-pointer border-round transition-colors"
          style={{
            fontSize: "0.8rem",
            color: "#6366f1",
            borderRadius: 6,
            transition: "all 0.12s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#eef2ff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          onClick={() => {
            if (onOpenCreate) onOpenCreate();
            else setShowNewDialog(true);
          }}
        >
          <span className="flex align-items-center" style={{ width: 10 }}>
            <span style={{ width: 8, height: 1, background: "#cbd5e1", display: "inline-block" }} />
          </span>
          <i className="pi pi-plus" style={{ fontSize: "0.65rem", fontWeight: 600 }} />
          <span style={{ fontWeight: 500 }}>{createLabel}</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-column" style={{ width: "100%" }}>
      {/* ── Header — click to expand/collapse ── */}
      <div
        className="flex align-items-center gap-2 px-3 py-2 cursor-pointer transition-colors"
        style={{
          userSelect: "none",
          fontSize: "0.875rem",
          fontWeight: 600,
          color: "#374151",
          borderRadius: 0,
          transition: "all 0.12s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f5f9"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        onClick={() => {
          setExpanded(!expanded);
          if (mergeHeader && items.length > 0 && onSelect) {
            onSelect(items[0]);
          }
        }}
      >
        <i
          className={`pi pi-chevron-${expanded ? "down" : "right"}`}
          style={{ fontSize: "0.65rem", color: "#94a3b8", transition: "transform 0.15s ease" }}
        />
        <i className={icon} style={{ fontSize: "0.85rem", color: "#64748b" }} />
        {mergeHeader && items.length > 0 ? (
          <span style={{ fontWeight: 700, fontSize: "0.875rem" }}>{items[0].label}</span>
        ) : (
          <span style={{ fontSize: "0.875rem" }}>{title}</span>
        )}
        {itemCount !== undefined && itemCount > 0 && (
          <span
            className="ml-auto"
            style={{
              fontSize: "0.65rem",
              fontWeight: 600,
              color: "#64748b",
              background: "#e2e8f0",
              borderRadius: 10,
              padding: "1px 7px",
              lineHeight: "18px",
            }}
          >
            {itemCount}
          </span>
        )}
      </div>

      {/* ── Items ── */}
      {expanded && (
        <div className="flex flex-column" style={{ gap: 1, padding: "0 8px" }}>
          {items.length === 0 && !showUngrouped && (
            <span
              className="px-2 py-1"
              style={{
                fontSize: "0.75rem",
                color: "#94a3b8",
                fontStyle: "italic",
                padding: "6px 12px",
              }}
            >
              No items yet
            </span>
          )}

          {items.map((item, index) => {
            // When mergeHeader, first item is merged into header — skip parent row but render children
            if (mergeHeader && index === 0) {
              return item.children && item.children.length > 0
                ? renderChildren(item.children, item.id)
                : null;
            }

            const hasChildren = item.children && item.children.length > 0;
            const isExpanded = expandedParents[item.id];

            return (
              <div key={item.id}>
                {/* ── Parent item row ── */}
                <div
                  className="flex align-items-center gap-1 px-2 py-1 border-round cursor-pointer transition-colors"
                  style={{
                    fontSize: "0.8rem",
                    borderRadius: 6,
                    position: "relative",
                    background: selectedId === item.id ? "#eef2ff" : "transparent",
                    color: selectedId === item.id ? "#4f46e5" : "#374151",
                    fontWeight: selectedId === item.id ? 600 : 400,
                    transition: "all 0.12s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedId !== item.id) e.currentTarget.style.background = "#f1f5f9";
                  }}
                  onMouseLeave={(e) => {
                    if (selectedId !== item.id) e.currentTarget.style.background = "transparent";
                  }}
                  onClick={() => {
                    if (hasChildren) toggleParent(item.id);
                    if (onSelect) onSelect(item);
                  }}
                >
                  {/* Expand/collapse chevron for items with children */}
                  {hasChildren ? (
                    <i
                      className={`pi pi-chevron-${isExpanded ? "down" : "right"}`}
                      style={{
                        fontSize: "0.55rem",
                        color: "#94a3b8",
                        cursor: "pointer",
                        transition: "transform 0.15s ease",
                      }}
                      onClick={(e) => { e.stopPropagation(); toggleParent(item.id); }}
                    />
                  ) : (
                    <span style={{ width: 10 }} />
                  )}

                  {/* Selection dot */}
                  {selectedId === item.id ? (
                    <i className="pi pi-circle-fill" style={{ fontSize: "0.45rem", color: "#4f46e5" }} />
                  ) : (
                    <i className="pi pi-circle" style={{ fontSize: "0.45rem", color: "#cbd5e1" }} />
                  )}

                  <span
                    className="flex-1"
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.label}
                  </span>

                  {/* Delete button */}
                  {hasDelete && item.deletable && (
                    <i
                      className="pi pi-trash cursor-pointer"
                      style={{
                        fontSize: "0.65rem",
                        color: selectedId === item.id ? "#a5b4fc" : "#94a3b8",
                        opacity: 0,
                        transition: "opacity 0.12s ease",
                      }}
                      onMouseEnter={(e) => { e.stopPropagation(); e.currentTarget.style.color = "#ef4444"; }}
                      onMouseLeave={(e) => { e.stopPropagation(); e.currentTarget.style.color = selectedId === item.id ? "#a5b4fc" : "#94a3b8"; }}
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirm(item); }}
                      title="Delete"
                    />
                  )}

                  {/* Active indicator */}
                  {selectedId === item.id && (
                    <span
                      style={{
                        position: "absolute",
                        left: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 3,
                        height: 16,
                        background: "#4f46e5",
                        borderRadius: "0 3px 3px 0",
                      }}
                    />
                  )}
                </div>

                {/* ── Children ── */}
                {hasChildren && isExpanded && renderChildren(item.children, item.id)}
              </div>
            );
          })}

          {/* Separator before "+ New Dataset" */}
          {(showUngrouped || (hasCreate && !createIndent)) && items.length > 0 && (
            <div style={{ borderTop: "1px solid #e2e8f0", margin: "4px 0" }} />
          )}

          {/* "+ New" button (root level — hidden when createIndent is used) */}
          {hasCreate && !createIndent && (
            <div
              className="flex align-items-center gap-2 px-2 py-1 cursor-pointer border-round transition-colors"
              style={{
                fontSize: "0.8rem",
                color: "#6366f1",
                borderRadius: 6,
                fontWeight: 500,
                transition: "all 0.12s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#eef2ff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              onClick={() => {
                if (onOpenCreate) onOpenCreate();
                else setShowNewDialog(true);
              }}
            >
              <i className="pi pi-plus" style={{ fontSize: "0.65rem", fontWeight: 600 }} />
              <span>{createLabel}</span>
            </div>
          )}

          {/* Empty state */}
          {items.length === 0 && !hasCreate && (
            <span
              className="px-2 py-1"
              style={{
                fontSize: "0.75rem",
                color: "#94a3b8",
                fontStyle: "italic",
                padding: "6px 12px",
              }}
            >
              No items yet
            </span>
          )}
        </div>
      )}

      {/* ── Create Dialog ── */}
      <Dialog
        header={hasCreate ? (createDialogTitle || `New ${title}`) : ""}
        visible={showNewDialog}
        onHide={() => setShowNewDialog(false)}
        style={{ width: 350 }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button label="Cancel" className="p-button-text" onClick={() => setShowNewDialog(false)} />
            <Button label="Create" icon="pi pi-check" onClick={handleCreate} disabled={!newName.trim()} />
          </div>
        }
      >
        <div className="flex flex-column gap-2">
          <label className="text-sm">Name</label>
          <InputText
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Dataset A"
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          />
        </div>
      </Dialog>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog
        header="Delete"
        visible={!!deleteConfirm}
        onHide={() => setDeleteConfirm(null)}
        style={{ width: 400 }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button label="Cancel" className="p-button-text" onClick={() => setDeleteConfirm(null)} />
            <Button
              label="Delete"
              icon="pi pi-trash"
              className="p-button-danger"
              onClick={() => handleDelete(deleteConfirm)}
            />
          </div>
        }
      >
        <p>
          Delete <strong>{deleteConfirm?.label}</strong>?<br />
          Items in this group will be unlinked.
        </p>
      </Dialog>
    </div>
  );
});

export default GroupSidebar;
